
import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Board } from "@/contexts/boards";
import { SidebarMenuButton } from "@/components/ui/sidebar/components/menu";
import { MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

interface BoardItemProps {
  board: Board;
  isActive: boolean;
  draggingId: string | null;
  dropTargetId: string | null;
  contentDropTargetId: string | null;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, id: string) => void;
  onDragEnd: () => void;
  onContentDragOver: (e: React.DragEvent, id: string) => void;
  onContentDrop: (e: React.DragEvent, id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const BoardItem: React.FC<BoardItemProps> = ({
  board,
  isActive,
  draggingId,
  dropTargetId,
  contentDropTargetId,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onContentDragOver,
  onContentDrop,
  onEdit,
  onDelete
}) => {
  const isDragging = draggingId === board.id;
  const isDropTarget = dropTargetId === board.id;
  const isContentDropTarget = contentDropTargetId === board.id;

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEdit(board.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(board.id);
  };

  return (
    <div
      draggable={true}
      onDragStart={(e) => onDragStart(e, board.id)}
      onDragOver={(e) => {
        onDragOver(e, board.id);
        onContentDragOver(e, board.id);
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        onDrop(e, board.id);
        onContentDrop(e, board.id);
      }}
      onDragEnd={onDragEnd}
      className={cn(
        "group/board-item transition-all duration-200 rounded-md",
        {
          "opacity-50": isDragging,
          "bg-accent/50": isDropTarget,
          "bg-blue-500/10 ring-1 ring-blue-500": isContentDropTarget,
        }
      )}
    >
      <SidebarMenuButton asChild isActive={isActive} className="w-full">
        <Link to={`/board/${board.id}`} className="w-full justify-between group/board-link">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: board.color || "#6B7789" }}
            />
            <span className="truncate text-sm font-medium">{board.title}</span>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger 
              asChild 
              onClick={(e) => e.stopPropagation()}
              className="opacity-0 group-hover/board-item:opacity-100 transition-opacity duration-200 shrink-0"
            >
              <button className="p-1 hover:bg-accent rounded-sm transition-colors">
                <MoreHorizontal size={14} className="text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <DropdownMenuItem onClick={handleEdit} className="text-sm">
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete} className="text-sm text-destructive">
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Link>
      </SidebarMenuButton>
    </div>
  );
};

export default BoardItem;
